import React, { useState } from 'react';
import { useChatAutoScroll } from '../hooks/useChatAutoScroll';
import { Modal, Button, TextInput, Alert, EmptyState, ConfirmDialog, Avatar } from '../components/ui';
import { zIndex } from '../design-system/tokens';

/**
 * Private 1:1 chat modal (opened from Friends / notifications).
 */
export function PrivateChatPanel({
  privateChatWith,
  onClose,
  onDeleteChat,
  onChatDeleted,
  messages,
  error,
  input,
  onInputChange,
  onSend,
  maxLength,
}) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  const {
    containerRef,
    endRef,
    scrollToBottom,
    markForceScroll,
    handleScroll,
    showJumpToLatest,
  } = useChatAutoScroll(messages, [privateChatWith?.id]);

  if (!privateChatWith) return null;

  const hasMessages = messages.length > 0;
  const canClear = Boolean(onDeleteChat) && hasMessages;

  const handleClose = () => {
    if (clearing) return;
    setShowClearConfirm(false);
    onClose?.();
  };

  const handleClearChat = async () => {
    if (!onDeleteChat || clearing) return;
    setClearing(true);
    try {
      const ok = await onDeleteChat();
      if (ok) {
        setShowClearConfirm(false);
        onChatDeleted?.(privateChatWith);
      }
    } finally {
      setClearing(false);
    }
  };

  const handleSend = () => {
    markForceScroll();
    onSend?.();
  };

  return (
    <>
      <Modal
        open
        onClose={handleClose}
        accent="brand"
        size="sm"
        z={zIndex.modal}
        scrimClassName="bg-black/70 backdrop-blur-sm"
        panelClassName="rounded-[1.75rem] max-h-[85vh]"
        bodyClassName="p-0 flex flex-col min-h-0"
        header={(
          <header className="shrink-0 flex items-center gap-2.5 px-4 py-3 border-b border-gray-800/80 bg-gray-900/98">
            <Button
              type="button"
              variant="close"
              onClick={handleClose}
              disabled={clearing}
              aria-label="Close chat"
              className="w-8 h-8 rounded-lg text-sm shrink-0"
            >
              ×
            </Button>
            <Avatar
              src={privateChatWith.avatar}
              name={privateChatWith.name}
              size="sm"
              fallback={<span aria-hidden>👤</span>}
              className="border border-gray-700"
            />
            <span className="text-xs font-black text-white truncate flex-1 min-w-0">
              {privateChatWith.name}
            </span>
            {canClear && (
              <Button
                type="button"
                variant="link"
                onClick={() => setShowClearConfirm(true)}
                disabled={clearing}
                aria-label="Clear chat history"
                className="shrink-0 text-[10px] uppercase tracking-widest text-gray-500 hover:text-red-400 px-2 py-1"
              >
                Clear
              </Button>
            )}
          </header>
        )}
        footer={(
          <div className="shrink-0 flex flex-col gap-1 p-3 border-t border-gray-800/80 bg-gray-900/98 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex gap-2">
              <TextInput
                type="text"
                placeholder="Type a message..."
                value={input}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                maxLength={maxLength}
                className="flex-1 mt-0 px-3 py-2.5 text-base sm:text-[13px] font-medium border-gray-800 focus:border-emerald-500/50 focus:ring-emerald-500/20"
              />
              <Button
                type="button"
                size="sm"
                onClick={handleSend}
                disabled={!input.trim() || clearing}
                className="shrink-0 px-4 py-2.5 text-[9px] shadow-[0_4px_14px_rgba(16,185,129,0.3)]"
              >
                Send
              </Button>
            </div>
            <p className="text-[9px] text-gray-600 font-bold uppercase tracking-widest text-right">
              {input.length}/{maxLength}
            </p>
          </div>
        )}
      >
        <div className="relative flex-1 min-h-[200px] max-h-[320px]">
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="absolute inset-0 overflow-y-auto p-3 flex flex-col gap-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {error && (
              <Alert tone="warning" className="text-[10px] font-bold py-2">
                {error}
              </Alert>
            )}
            {!error && messages.length === 0 && (
              <EmptyState
                variant="simple"
                description="No messages yet. Say something to start the conversation."
                className="py-8 text-[11px]"
              />
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={msg.fromMe ? 'self-end max-w-[85%]' : 'self-start max-w-[85%]'}
              >
                <div
                  className={`px-3 py-2 rounded-xl ${msg.fromMe ? 'bg-emerald-500/25 border border-emerald-500/40' : 'bg-gray-800/90 border border-gray-700/80'}`}
                >
                  <p className="text-[13px] font-medium text-white leading-snug">{msg.text}</p>
                  <p className="text-[9px] text-gray-500 mt-0.5">{msg.time}</p>
                </div>
              </div>
            ))}
            <div ref={endRef} className="h-px shrink-0" aria-hidden />
          </div>
          {showJumpToLatest && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => scrollToBottom('smooth')}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full border-emerald-500/40 text-[10px] text-emerald-400 shadow-lg min-h-0"
            >
              Latest messages
            </Button>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={showClearConfirm}
        onClose={() => {
          if (clearing) return;
          setShowClearConfirm(false);
        }}
        onConfirm={handleClearChat}
        confirming={clearing}
        accent="danger"
        icon="🗑️"
        iconWrapClassName="w-12 h-12 text-2xl"
        iconClassName="rounded-2xl bg-red-500/10 border-2 border-red-500/30"
        title="Clear chat history?"
        message="This permanently removes the conversation for both players."
        summary={[
          { label: 'Conversation', value: privateChatWith.name, highlight: true },
          { label: 'Messages', value: `${messages.length} message${messages.length === 1 ? '' : 's'}` },
          { label: 'Effect', value: 'Removed for both players' },
        ]}
        footerNote="This cannot be undone."
        confirmLabel="Clear chat"
        confirmingLabel="Clearing…"
        confirmVariant="destructive"
        cancelLabel="Keep messages"
      />
    </>
  );
}
